import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { ROUTES } from "../config/routes";
import { appSettings } from "../config/appSettings";
import { ApplicationByIdResponse } from "../api/generated/models/ApplicationByIdResponse";
import { OnboardingStepResult } from "../api/generated/models/OnboardingStepResult";
import { PartnerOnboardingService } from "../api/generated/services/PartnerOnboardingService";
import ApproveApplicationModal from "../components/PartnerManagement/modals/ApproveApplicationModal";
import RejectApplicationModal from "../components/PartnerManagement/modals/RejectApplicationModal";
import RetryOnboardingModal from "../components/PartnerManagement/modals/RetryOnboardingModal";
import { ReactComponent as ArrowRight } from "../assets/icons/arrow-right.svg";
import { ReactComponent as RefreshIcon } from "../assets/icons/refresh.svg";
import "../api/openApiConfig";

const PartnerApplicationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [application, setApplication] =
    useState<ApplicationByIdResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isRetryModalOpen, setIsRetryModalOpen] = useState(false);

  const isRefreshingRef = useRef(false);

  const fetchApplication = useCallback(async (isAutoRefresh = false) => {
    if (!id) return;
    // Prevent simultaneous refreshes
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      if (!isAutoRefresh) setLoading(true);
      const response =
        await PartnerOnboardingService.getApiPartnerOnboardingApplication(id);
      setApplication(response);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching application:", err);
      if (!isAutoRefresh) {
        if (err?.status === 404) {
          setError("Application not found.");
        } else {
          setError("Failed to load application. Please try again.");
        }
      }
    } finally {
      if (!isAutoRefresh) setLoading(false);
      isRefreshingRef.current = false;
    }
  }, [id]);

  // Check if application is in progress for auto-refresh
  const isAppInProgress = (() => {
    const statusLower = application?.status?.toLowerCase();
    return statusLower === "inprogress" || statusLower === "in progress";
  })();

  // Initial fetch
  useEffect(() => {
    if (id) {
      fetchApplication();
    }
  }, [id, fetchApplication]);

  // Auto-refresh every 5 seconds while in progress
  useEffect(() => {
    if (!isAppInProgress) return;

    const intervalId = setInterval(() => {
      fetchApplication(true);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isAppInProgress, fetchApplication]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const handleBack = () => {
    navigate(ROUTES.ADMIN_PARTNER_MANAGEMENT());
  };

  const handleApproveSuccess = () => {
    setSuccessMessage("Application approved successfully.");
    fetchApplication();
  };

  const handleRejectSuccess = () => {
    setSuccessMessage("Application rejected successfully.");
    fetchApplication();
  };

  const renderStepResult = (
    label: string,
    result?: OnboardingStepResult,
    isApplicationCompleted?: boolean,
    link?: { href: string; text: string }
  ) => {
    const renderLink = () =>
      link && (
        <a
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
        >
          {link.text}
        </a>
      );

    if (!result) {
      return (
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-gray-600">{label}</span>
          <span className="text-gray-400">-</span>
        </div>
      );
    }

    // If completed with error, show as error
    if (result.completed && result.error) {
      return (
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-gray-600">{label}</span>
          <div className="flex flex-col items-end">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Error
            </span>
            <span className="text-xs text-red-600 mt-1">{result.error}</span>
            {renderLink()}
          </div>
        </div>
      );
    }

    // Completed successfully
    if (result.completed) {
      return (
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-gray-600">{label}</span>
          <div className="flex flex-col items-end">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Completed
            </span>
            {renderLink()}
          </div>
        </div>
      );
    }

    // Not completed with error (failed)
    if (result.error) {
      return (
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-gray-600">{label}</span>
          <div className="flex flex-col items-end">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Failed
            </span>
            <span className="text-xs text-red-600 mt-1">{result.error}</span>
            {renderLink()}
          </div>
        </div>
      );
    }

    // Skipped (application completed but step not completed)
    if (isApplicationCompleted) {
      return (
        <div className="flex items-center justify-between py-2 border-b border-gray-100">
          <span className="text-gray-600">{label}</span>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Skipped
          </span>
        </div>
      );
    }

    // Pending
    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <span className="text-gray-600">{label}</span>
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Pending
        </span>
      </div>
    );
  };

  const renderStatusBadge = () => {
    if (!application?.status) return null;

    const statusLower = application.status.toLowerCase();
    if (statusLower === "completed") {
      if (application.hasErrors) {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            Completed with Errors
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          Completed
        </span>
      );
    }
    if (statusLower === "inprogress" || statusLower === "in progress") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
          In Progress
        </span>
      );
    }
    if (statusLower === "rejected") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          Rejected
        </span>
      );
    }
    if (statusLower === "pending") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
        {application.status}
      </span>
    );
  };

  // Determine action visibility based on status
  const isCompleted =
    application?.status?.toLowerCase() === "completed";
  const isRejected =
    application?.status?.toLowerCase() === "rejected";
  const isInProgress =
    application?.status?.toLowerCase() === "inprogress" ||
    application?.status?.toLowerCase() === "in progress";

  if (loading) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  if (error && !application) {
    return (
      <div>
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-2 gap-2 text-gray-600 justify-start pl-0"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          Back to Partner Applications
        </Button>
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div>
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-2 gap-2 text-gray-600 justify-start pl-0"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          Back to Partner Applications
        </Button>
        <div className="text-center text-gray-500 my-8">
          Application not found
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={handleBack}
        className="mb-2 gap-2 text-gray-600 justify-start pl-0"
      >
        <ArrowRight className="w-4 h-4 rotate-180" />
        Back to Partner Applications
      </Button>

      {/* Header */}
      <div className="flex max-md:flex-col md:items-center md:justify-between mb-8 max-md:gap-4">
        <div className="flex items-center gap-3">
          <h2 className="font-medium">Application Details</h2>
          {renderStatusBadge()}
          <Button
            onClick={() => fetchApplication()}
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={loading}
          >
            <RefreshIcon className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {application?.hasErrors && (
            <Button
              onClick={() => setIsRetryModalOpen(true)}
              variant="danger"
              size="sm"
            >
              Retry
            </Button>
          )}
        </div>
        {/* Action Buttons */}
        {!isCompleted && (
          <div className="flex gap-3">
            {!isInProgress && !isRejected && (
              <Button
                onClick={() => setIsRejectModalOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Reject
              </Button>
            )}
            <Button
              onClick={() => setIsApproveModalOpen(true)}
              disabled={isInProgress}
              isLoading={isInProgress}
            >
              {isInProgress ? "Processing..." : "Approve"}
            </Button>
          </div>
        )}
      </div>

      {/* Success Notification */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Notification */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Application Details */}
      <div className="relative">
        {isInProgress && (
          <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg">
            <Loader />
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">
            Company Information
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Company Name</span>
              <span className="font-medium">
                {application.companyName || "-"}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">CVR</span>
              <span className="font-medium">{application.cvr || "-"}</span>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">
            Contact Information
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Contact Name</span>
              <span className="font-medium">
                {application.contactName || "-"}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Email</span>
              <span className="font-medium">{application.email || "-"}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Phone Number</span>
              <span className="font-medium">
                {application.phoneNumber || "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">Timeline</h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Source</span>
              <span className="font-medium">
                {application.source || "-"}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Submitted At</span>
              <span className="font-medium">
                {formatDate(application.submittedAt)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Approved At</span>
              <span className="font-medium">
                {formatDate(application.approvedAt)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Rejected At</span>
              <span className="font-medium">
                {formatDate(application.rejectedAt)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Completed At</span>
              <span className="font-medium">
                {formatDate(application.completedAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Onboarding Steps */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium mb-4 text-gray-800">
            Onboarding Steps
          </h3>
          <div className="space-y-1">
            {renderStepResult(
              "Billing Configuration",
              application.billingConfigurationResult,
              isCompleted,
              application.billwerkCustomerHandle
                ? {
                    href: `${appSettings.BILLWERK_CUSTOMER_BASE_URL}/${application.billwerkCustomerHandle}`,
                    text: "See in Billwerk",
                  }
                : undefined
            )}
            {renderStepResult(
              "Company Creation",
              application.companyCreationResult,
              isCompleted,
              application.contentfulCompanyId
                ? {
                    href: `${process.env.REACT_APP_CONTENTFUL_BASE_URL}/entries/${application.contentfulCompanyId}`,
                    text: "See in Contentful",
                  }
                : undefined
            )}
            {renderStepResult(
              "Company Publishing",
              application.companyPublishingResult,
              isCompleted
            )}
            {renderStepResult(
              "Admin Invitation",
              application.adminInvitationResult,
              isCompleted
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Partner Details Link */}
      {isCompleted && application.contentfulCompanyId && (
        <div className="flex justify-end mt-6">
          <Button
            onClick={() => navigate(ROUTES.ADMIN_PARTNER_DETAILS(application.contentfulCompanyId!))}
          >
            Partner Details
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Modals */}
      <ApproveApplicationModal
        isOpen={isApproveModalOpen}
        applicationId={application.partnerApplicationId || ""}
        companyName={application.companyName || ""}
        onClose={() => setIsApproveModalOpen(false)}
        onSuccess={handleApproveSuccess}
      />

      <RejectApplicationModal
        isOpen={isRejectModalOpen}
        applicationId={application.partnerApplicationId || ""}
        companyName={application.companyName || ""}
        onClose={() => setIsRejectModalOpen(false)}
        onSuccess={handleRejectSuccess}
      />

      <RetryOnboardingModal
        isOpen={isRetryModalOpen}
        applicationId={application.partnerApplicationId || ""}
        companyName={application.companyName || ""}
        onClose={() => setIsRetryModalOpen(false)}
        onSuccess={() => {
          setSuccessMessage("Onboarding process is being retried. This may take a few seconds.");
          fetchApplication();
        }}
      />
    </div>
  );
};

export default PartnerApplicationDetails;
